/**
 * Servicio para gestión de clientes.
 *
 * IMPORTANTE: Todos los métodos reciben el cliente Supabase autenticado como primer parámetro.
 * Usar con useSupabase() desde el hook que llame a estos métodos.
 *
 * @example
 * const { supabase } = useSupabase()
 * const clientes = await clientesService.listar(supabase!, filtros)
 */
import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database, ClienteConFacetaContable, Movimiento, ActividadEconomica, Json } from '@/types/database'

// Tipos derivados de las vistas/tablas generadas (antes estaban como alias en database.ts)
type VistaSaldoCliente = Database['public']['Views']['vista_saldos_clientes']['Row']
type Cliente = Database['public']['Tables']['clientes']['Row']
import type { ImpuestoLiquidar } from '@/types/impuestos'
import { impuestosClienteService } from './impuestosClienteService'
import { personasService } from './personasService'
import { preciosDeAsignaciones } from './serviciosService'

// Tipo para servicio activo del cliente
export interface ServicioActivoCliente {
  servicio_id: string
  nombre: string
  precio: number
  frecuencia: 'mensual' | 'bimestral' | 'trimestral' | 'semestral' | 'anual' | 'unico'
}

/** Columnas de `vista_saldos_clientes` sin saldo, debe, haber ni honorarios. */
const COLUMNAS_VISTA_SIN_MONTOS =
  'id, numero_cliente, fecha_alta, organizacion_id, nombre_formateado, nombre, tipo_persona, cuit, admin_cuit, email, telefono, provincia, condicion_iva, cierre_ejercicio, actividad_principal, codigo_actividad_afip, impuestos_liquidar, responsable, estado, descripcion'

/** El saldo sale de movimientos y los honorarios de los servicios: cada uno con su permiso (ORR-613). */
const columnasVista = (filtros?: { conMontos?: boolean; conSaldo?: boolean }) =>
  [
    COLUMNAS_VISTA_SIN_MONTOS,
    ...(filtros?.conSaldo ? ['total_debe, total_haber, saldo'] : []),
    ...(filtros?.conMontos ? ['honorarios_mensuales'] : []),
  ].join(', ')

// Tipo extendido con campos adicionales para la tabla mejorada
export type VistaSaldoClienteConImpuestos = Omit<VistaSaldoCliente, 'impuestos_liquidar'> & {
  impuestos_liquidar?: ImpuestoLiquidar[] | null
  nombre_formateado?: string | null
  tipo_persona?: 'fisica' | 'juridica' | 'sucesion_indivisa' | null
  condicion_iva?: string | null
  cierre_ejercicio?: string | null
  actividad_principal?: string | null
  codigo_actividad_afip?: string | null
  provincia?: string | null
  fecha_alta?: string | null
  descripcion?: string | null
  admin_cuit?: string | null
  admin_nombre?: string | null
  servicios_activos?: ServicioActivoCliente[]
}

export interface FiltrosClientes {
  estado?: string
  busqueda?: string
  /** ORR-128: si está presente, devuelve solo los clientes donde este usuario es responsable (scoping asistente). */
  responsableUsuarioId?: string
  /** Con `true` trae honorarios y el precio de cada servicio. Sin él no los pide y el precio llega en 0. */
  conMontos?: boolean
  /** Con `true` trae saldo, debe y haber, que salen de movimientos (ORR-613). Sin él no los pide. */
  conSaldo?: boolean
}

/** A row of `impuestos_cliente` as `obtenerPorId` embeds it, with the catalog code of its tax. */
export interface AsignacionImpuestoCliente {
  impuesto_id: string
  activo: boolean | null
  impuesto: { codigo: string } | null
}

/** The client `obtenerPorId` returns: persona, faceta and every `impuestos_cliente` row, active or not. */
export type ClienteConImpuestosAsignados = ClienteConFacetaContable & {
  impuestos_cliente: AsignacionImpuestoCliente[]
}

/** ORR-120: name-only fields used to build a presupuesto filename (no secrets). */
export interface ClienteDatosNombre {
  tipo: string | null
  nombre: string | null
  apellido: string | null
  razon_social: string | null
  nombre_completo: string | null
}

// Tipo para crear un cliente completo (persona + cliente + faceta_contable)
export interface ClienteCompletoInput {
  // Datos de persona
  tipo_persona?: 'fisica' | 'juridica' | 'sucesion_indivisa'
  nombre: string
  apellido?: string
  nombre_fantasia?: string | null
  cuit?: string | number | null
  email?: string | null
  telefono?: string | null
  // Datos de domicilio
  domicilio_calle?: string | null
  domicilio_numero?: string | null
  domicilio_piso?: string | null
  domicilio_depto?: string | null
  domicilio_cp?: string | null
  domicilio_localidad?: string | null
  domicilio_provincia?: string | null
  domicilio_barrio?: string | null
  domicilio_referencia?: string | null
  // Administrador (solo para sucesion indivisa)
  admin_cuit?: string | null
  admin_nombre?: string | null
  // Audit
  created_by?: string | null
  // Datos de cliente
  organizacion_id?: string | null
  estado?: 'activo' | 'inactivo' | 'suspendido' | 'potencial'
  fecha_alta?: string | null
  // ORR-404: motivo de la baja. La base los limpia sola al reactivar, así que reactivar
  // no necesita mandarlos en null.
  motivo_baja?: string | null
  motivo_baja_detalle?: string | null
  responsables?: string[]
  descripcion?: string | null
  // Datos de faceta contable
  condicion_iva?: string | null
  categoria_monotributo?: string | null
  actividad_principal?: string | null
  codigo_actividad_afip?: string | null
  actividades_economicas?: ActividadEconomica[] | null
  cierre_ejercicio?: string | null
  honorarios_mensuales?: number | null
  clave_fiscal?: string | null
  provincia_principal?: string | null
  impuestos_inscriptos?: string[] | null
  regimen_iibb?: 'local' | 'convenio_multilateral' | 'simplificado' | 'no_gravado' | null
  impuestos_liquidar?: ImpuestoLiquidar[] | null
  // Datos de Inscripción (solo juridica)
  inscripcion_numero?: string | null
  inscripcion_fojas?: string | null
  inscripcion_tomo?: string | null
  inscripcion_protocolo?: string | null
  inscripcion_fecha?: string | null
  inscripcion_ano?: number | null
  // Servicios y deudas que el alta guarda en la misma transacción que el cliente
  servicios?: ServicioAlta[]
  deudas?: DeudaAlta[]
}

/** Servicio que el alta le asigna al cliente. */
export interface ServicioAlta {
  servicio_id: string
  precio: number
  frecuencia?: string
  mes_inicio?: number
}

/**
 * Deuda que el alta le carga al cliente. La 'mensual' es la del lote y la base exige que coincida
 * con un servicio del alta. La 'esporadica' usa un servicio de periodicidad única.
 */
export interface DeudaAlta {
  servicio_id: string
  concepto: string
  monto: number
  tipo_deuda: 'mensual' | 'esporadica'
  mes_anio: string | null
  fecha?: string
}

type ClienteInsert = Database['public']['Tables']['clientes']['Insert']
type ClienteUpdate = Database['public']['Tables']['clientes']['Update']

// Mapear condicion fiscal del formulario a valor de la DB
function mapCondicionFiscal(condicion: string | null | undefined): string | null {
  if (!condicion) return null

  const validCondiciones = [
    'responsable_inscripto',
    'monotributo',
    'exento',
    'consumidor_final',
    'no_responsable',
    'monotributo_social'
  ]
  if (validCondiciones.includes(condicion)) {
    return condicion
  }

  const mapping: Record<string, string> = {
    'Responsable Inscripto': 'responsable_inscripto',
    'Monotributista': 'monotributo',
    'Exento': 'exento',
    'Consumidor Final': 'consumidor_final',
  }
  return mapping[condicion] || condicion.toLowerCase().replace(/ /g, '_')
}

// Mapear estado del formulario a valor de la DB
function mapEstado(estado: string | null | undefined): string {
  if (!estado) return 'activo'
  const mapping: Record<string, string> = {
    'Activo': 'activo',
    'Inactivo': 'inactivo',
    'Suspendido': 'suspendido',
  }
  return mapping[estado] || estado.toLowerCase()
}

export const clientesService = {
  async listar(client: SupabaseClient<Database>, filtros?: FiltrosClientes): Promise<VistaSaldoClienteConImpuestos[]> {
    let query = client
      .from('vista_saldos_clientes')
      // El tipo es el de la fila completa. Las columnas que no se piden llegan undefined.
      .select(columnasVista(filtros) as '*')
      .order('nombre')

    if (filtros?.estado) {
      const estadoDb = mapEstado(filtros.estado)
      query = query.eq('estado', estadoDb)
    }
    if (filtros?.busqueda) {
      query = query.or(`nombre.ilike.%${filtros.busqueda}%,cuit.ilike.%${filtros.busqueda}%`)
    }

    // ORR-128: scoping por responsable (el rol asistente solo ve sus clientes asignados).
    if (filtros?.responsableUsuarioId) {
      const { data: rels, error: relErr } = await client
        .from('cliente_responsables')
        .select('cliente_id')
        .eq('usuario_id', filtros.responsableUsuarioId)
      if (relErr) throw relErr
      const idsResponsable = (rels ?? []).map((r) => r.cliente_id)
      if (idsResponsable.length === 0) return []
      query = query.in('id', idsResponsable)
    }

    const { data: clientesData, error } = await query
    if (error) throw error

    const clienteIds = (clientesData || [])
      .map((c: { id: string | null }) => c.id)
      .filter((id): id is string => id !== null)

    if (clienteIds.length === 0) {
      return clientesData as VistaSaldoClienteConImpuestos[]
    }

    // Read impuestos from relational table (impuestos_cliente) instead of JSONB
    const { data: impuestosClienteData } = await client
      .from('impuestos_cliente')
      .select('cliente_id, impuesto_id, fecha_inscripcion, catalogo_impuestos:impuesto_id(id, codigo, nombre_corto, color)')
      .eq('activo', true)
      .in('cliente_id', clienteIds)

    const impuestosMap = new Map<string, ImpuestoLiquidar[] | null>()
    if (impuestosClienteData) {
      for (const row of impuestosClienteData) {
        const cat = row.catalogo_impuestos as unknown as { id: string; codigo: string; nombre_corto: string | null; color: string | null } | null
        if (!cat) continue
        const arr = impuestosMap.get(row.cliente_id) ?? []
        arr.push({ impuesto_id: cat.id, codigo: cat.codigo, fecha_inscripcion: row.fecha_inscripcion ?? null })
        impuestosMap.set(row.cliente_id, arr)
      }
    }

    // Obtener servicios activos con nombres
    const { data: serviciosData } = await client
      .from('cliente_servicio_precios')
      .select('id, cliente_id, servicio_id, frecuencia, servicios(nombre)')
      .eq('activo', true)
      .in('cliente_id', clienteIds)

    // Con conMontos el precio sale de vista_precios_servicios_cliente. Sin él llega en 0.
    const precios = filtros?.conMontos && serviciosData
      ? await preciosDeAsignaciones(client, serviciosData.map(s => s.id))
      : null
    const serviciosMap = new Map<string, ServicioActivoCliente[]>()
    if (serviciosData) {
      for (const s of serviciosData) {
        const serviciosCliente = serviciosMap.get(s.cliente_id) || []
        serviciosCliente.push({
          servicio_id: s.servicio_id,
          nombre: (s.servicios as { nombre: string } | null)?.nombre || 'Sin nombre',
          precio: precios?.get(s.id) ?? 0,
          frecuencia: s.frecuencia as ServicioActivoCliente['frecuencia'],
        })
        serviciosMap.set(s.cliente_id, serviciosCliente)
      }
    }

    const clientesConImpuestos: VistaSaldoClienteConImpuestos[] = (clientesData || []).map(
      (cliente: VistaSaldoCliente) => ({
        ...cliente,
        impuestos_liquidar: cliente.id ? impuestosMap.get(cliente.id) : null,
        servicios_activos: cliente.id ? serviciosMap.get(cliente.id) || [] : [],
      } as VistaSaldoClienteConImpuestos)
    )

    return clientesConImpuestos
  },

  /** Con `conMontos` trae `honorarios_mensuales` de la faceta contable. Sin él no lo pide. */
  async obtenerPorId(
    client: SupabaseClient<Database>,
    id: string,
    opciones: { conMontos?: boolean } = {}
  ): Promise<ClienteConImpuestosAsignados> {
    const { data, error } = await client
      .from('clientes')
      .select(`
        *,
        persona:personas!persona_id(
          id,
          tipo,
          nombre,
          apellido,
          razon_social,
          nombre_completo,
          nombre_fantasia,
          cuit,
          email,
          telefono,
          domicilio_calle,
          domicilio_numero,
          domicilio_piso,
          domicilio_depto,
          domicilio_cp,
          domicilio_localidad,
          domicilio_provincia,
          domicilio_barrio,
          domicilio_referencia,
          created_by,
          created_at,
          updated_at,
          admin_cuit,
          admin_nombre
        ),
        responsables:cliente_responsables!cliente_responsables_cliente_id_fkey(usuario_id),
        faceta_contable:cliente_faceta_contable!cliente_id(
          id,
          condicion_iva,
          categoria_monotributo,
          actividad_principal,
          codigo_actividad_afip,
          actividades_economicas,
          cierre_ejercicio,
          impuestos_inscriptos,
          clave_fiscal,
          otras_claves,
          ${opciones.conMontos ? 'honorarios_mensuales,' : ''}
          honorarios_desde,
          provincia_principal,
          regimen_iibb,
          impuestos_liquidar,
          inscripcion_numero,
          inscripcion_fojas,
          inscripcion_tomo,
          inscripcion_protocolo,
          inscripcion_fecha,
          inscripcion_ano
        ),
        impuestos_cliente:impuestos_cliente!impuestos_cliente_cliente_id_fkey(
          impuesto_id,
          activo,
          impuesto:catalogo_impuestos(codigo)
        )
      `)
      .eq('id', id)
      .single()

    if (error) throw error
    return data as unknown as ClienteConImpuestosAsignados
  },

  /**
   * Lightweight name-only lookup (ORR-120): returns just the persona name fields
   * for building filenames. Unlike obtenerPorId it does NOT pull faceta_contable
   * (clave_fiscal and other secrets), so it's safe to load just for a filename.
   */
  async obtenerDatosNombre(client: SupabaseClient<Database>, id: string): Promise<ClienteDatosNombre | null> {
    const { data, error } = await client
      .from('clientes')
      .select('persona:personas!persona_id(tipo, nombre, apellido, razon_social, nombre_completo)')
      .eq('id', id)
      .single()

    if (error) throw error
    return (data?.persona ?? null) as ClienteDatosNombre | null
  },

  /** Only the client's estado, without faceta_contable secrets. */
  async obtenerEstado(client: SupabaseClient<Database>, id: string): Promise<string | null> {
    const { data, error } = await client.from('clientes').select('estado').eq('id', id).single()

    if (error) throw error
    return data.estado
  },

  async crearCompleto(client: SupabaseClient<Database>, input: ClienteCompletoInput): Promise<Cliente> {
    if (!input.organizacion_id) throw new Error('Falta la organización del cliente')

    const tipoPersona = input.tipo_persona || 'juridica'
    const esJuridica = tipoPersona === 'juridica'
    const esSucesion = tipoPersona === 'sucesion_indivisa'

    const cuitStr = input.cuit
      ? (typeof input.cuit === 'number' ? input.cuit.toString() : input.cuit.toString().replace(/\D/g, ''))
      : null

    // Validar CUIT único antes de crear
    if (cuitStr && input.organizacion_id) {
      const verificacion = await personasService.verificarCUITDisponible(
        client,
        cuitStr,
        input.organizacion_id
      )
      if (!verificacion.disponible) {
        throw new Error(`CUIT_DUPLICADO:${verificacion.clienteExistente?.nombre || 'Cliente existente'}`)
      }
    }

    let nombreFinal = input.nombre
    let apellidoFinal = input.apellido || null
    if (!esJuridica && !apellidoFinal && input.nombre.includes(' ')) {
      const nombrePartes = input.nombre.trim().split(' ')
      nombreFinal = nombrePartes[0]
      apellidoFinal = nombrePartes.slice(1).join(' ') || null
    }

    // La persona, el cliente, la faceta contable, los servicios y las deudas se crean en una sola
    // transacción: si falla cualquiera, no queda nada. La RPC exige crearCliente en la organización,
    // editarHonorariosCliente si hay servicios y crearMovimiento si hay deudas. Todas las filas toman
    // la organización de p_organizacion_id, y created_by sale del usuario que llama.
    const provinciaParaIIBB = input.regimen_iibb === 'local'
      ? (input.domicilio_provincia || input.provincia_principal)
      : input.provincia_principal

    const { data: nuevoClienteId, error: errorAlta } = await client.rpc('crear_cliente_con_deuda', {
      p_organizacion_id: input.organizacion_id,
      p_persona: {
        tipo: tipoPersona,
        razon_social: esJuridica ? input.nombre : null,
        nombre: esJuridica ? null : nombreFinal,
        apellido: esJuridica ? null : apellidoFinal,
        nombre_fantasia: esJuridica ? (input.nombre_fantasia || null) : null,
        cuit: cuitStr,
        email: input.email || null,
        telefono: input.telefono || null,
        domicilio_calle: input.domicilio_calle || null,
        domicilio_numero: input.domicilio_numero || null,
        domicilio_piso: input.domicilio_piso || null,
        domicilio_depto: input.domicilio_depto || null,
        domicilio_cp: input.domicilio_cp || null,
        domicilio_localidad: input.domicilio_localidad || null,
        domicilio_provincia: input.domicilio_provincia || input.provincia_principal || null,
        domicilio_barrio: input.domicilio_barrio || null,
        domicilio_referencia: input.domicilio_referencia || null,
        admin_cuit: (esSucesion || esJuridica) ? (input.admin_cuit || null) : null,
        admin_nombre: (esSucesion || esJuridica) ? (input.admin_nombre || null) : null,
      },
      p_cliente: {
        estado: mapEstado(input.estado),
        fecha_alta: input.fecha_alta || new Date().toISOString().split('T')[0],
        descripcion: input.descripcion || null,
      },
      // ActividadEconomica e ImpuestoLiquidar son interfaces sin index signature, y TypeScript no
      // las acepta como Json aunque sus valores sean JSON válido.
      p_faceta: {
        condicion_iva: mapCondicionFiscal(input.condicion_iva),
        categoria_monotributo: input.categoria_monotributo || null,
        actividad_principal: input.actividad_principal || null,
        codigo_actividad_afip: input.codigo_actividad_afip || null,
        actividades_economicas: (input.actividades_economicas || []) as unknown as Json,
        cierre_ejercicio: input.cierre_ejercicio || null,
        honorarios_mensuales: input.honorarios_mensuales || 0,
        clave_fiscal: input.clave_fiscal || null,
        provincia_principal: provinciaParaIIBB || null,
        impuestos_inscriptos: input.impuestos_inscriptos || null,
        regimen_iibb: input.regimen_iibb || null,
        impuestos_liquidar: (input.impuestos_liquidar || []) as unknown as Json,
        // Datos de Inscripción
        inscripcion_numero: input.inscripcion_numero || null,
        inscripcion_fojas: input.inscripcion_fojas || null,
        inscripcion_tomo: input.inscripcion_tomo || null,
        inscripcion_protocolo: input.inscripcion_protocolo || null,
        inscripcion_fecha: input.inscripcion_fecha || null,
        inscripcion_ano: input.inscripcion_ano || null,
      },
      p_servicios: (input.servicios ?? []) as unknown as Json,
      p_deudas: (input.deudas ?? []) as unknown as Json,
    })

    if (errorAlta) throw errorAlta

    const { data: clienteData, error: errorFetchCliente } = await client
      .from('clientes')
      .select()
      .eq('id', nuevoClienteId)
      .single()

    if (errorFetchCliente) {
      throw errorFetchCliente
    }

    // Sincronizar impuestos con tabla impuestos_cliente
    if (input.impuestos_liquidar && input.impuestos_liquidar.length > 0) {
      const impuestoIds = input.impuestos_liquidar.map((i) => i.impuesto_id)
      const fechasInscripcion = Object.fromEntries(
        input.impuestos_liquidar
          .filter((i) => i.fecha_inscripcion)
          .map((i) => [i.impuesto_id, i.fecha_inscripcion ?? null])
      )
      await impuestosClienteService.sincronizar(client, clienteData.id, impuestoIds, fechasInscripcion)
    }

    // Sync responsables
    if (input.responsables && input.responsables.length > 0) {
      await this.sincronizarResponsables(client, clienteData.id, input.responsables)
    }

    return clienteData as Cliente
  },

  async actualizarCompleto(client: SupabaseClient<Database>, clienteId: string, input: Partial<ClienteCompletoInput>): Promise<Cliente> {
    const clienteActual = await this.obtenerPorId(client, clienteId)

    const facetaContable = Array.isArray(clienteActual.faceta_contable)
      ? clienteActual.faceta_contable[0]
      : clienteActual.faceta_contable

    const tipoPersona = input.tipo_persona || clienteActual.persona?.tipo || 'juridica'
    const esJuridica = tipoPersona === 'juridica'
    const esSucesion = tipoPersona === 'sucesion_indivisa'

    const cuitStr = input.cuit
      ? (typeof input.cuit === 'number' ? input.cuit.toString() : input.cuit.toString().replace(/\D/g, ''))
      : null

    // Validar CUIT único antes de actualizar (excluyendo el cliente actual)
    if (cuitStr && clienteActual.organizacion_id) {
      const verificacion = await personasService.verificarCUITDisponible(
        client,
        cuitStr,
        clienteActual.organizacion_id,
        clienteId // excluir el cliente actual
      )
      if (!verificacion.disponible) {
        throw new Error(`CUIT_DUPLICADO:${verificacion.clienteExistente?.nombre || 'Cliente existente'}`)
      }
    }

    // 1. Actualizar persona
    if (clienteActual.persona?.id && input.nombre) {
      let nombreFinal = input.nombre
      let apellidoFinal = input.apellido || null
      if (!esJuridica && !apellidoFinal && input.nombre.includes(' ')) {
        const nombrePartes = input.nombre.trim().split(' ')
        nombreFinal = nombrePartes[0]
        apellidoFinal = nombrePartes.slice(1).join(' ') || null
      }

      const personaUpdateData: Record<string, unknown> = {
        tipo: tipoPersona,
        razon_social: esJuridica ? input.nombre : null,
        nombre: esJuridica ? null : nombreFinal,
        apellido: esJuridica ? null : apellidoFinal,
        nombre_fantasia: esJuridica ? (input.nombre_fantasia || null) : null,
        cuit: cuitStr,
        email: input.email || null,
        telefono: input.telefono || null,
      }

      if (input.domicilio_calle !== undefined) personaUpdateData.domicilio_calle = input.domicilio_calle
      if (input.domicilio_numero !== undefined) personaUpdateData.domicilio_numero = input.domicilio_numero
      if (input.domicilio_piso !== undefined) personaUpdateData.domicilio_piso = input.domicilio_piso
      if (input.domicilio_depto !== undefined) personaUpdateData.domicilio_depto = input.domicilio_depto
      if (input.domicilio_cp !== undefined) personaUpdateData.domicilio_cp = input.domicilio_cp
      if (input.domicilio_localidad !== undefined) personaUpdateData.domicilio_localidad = input.domicilio_localidad
      if (input.domicilio_provincia !== undefined) {
        personaUpdateData.domicilio_provincia = input.domicilio_provincia
      } else if (input.provincia_principal !== undefined) {
        personaUpdateData.domicilio_provincia = input.provincia_principal
      }
      if (input.domicilio_barrio !== undefined) personaUpdateData.domicilio_barrio = input.domicilio_barrio
      if (input.domicilio_referencia !== undefined) personaUpdateData.domicilio_referencia = input.domicilio_referencia

      personaUpdateData.admin_cuit = (esSucesion || esJuridica) ? (input.admin_cuit || null) : null
      personaUpdateData.admin_nombre = (esSucesion || esJuridica) ? (input.admin_nombre || null) : null

      const { error: errorPersona } = await client
        .from('personas')
        .update(personaUpdateData as never)
        .eq('id', clienteActual.persona.id)

      if (errorPersona) throw errorPersona
    }

    // 2. Actualizar cliente
    // estado solo se aplica si vino en el input parcial: mapEstado(undefined)
    // devuelve 'activo', así que setearlo incondicionalmente pisaría el estado
    // real en updates parciales (ej. cambio de un solo campo).
    const updateData: Record<string, unknown> = {}
    if (input.estado !== undefined) {
      updateData.estado = mapEstado(input.estado)
    }

    if (input.fecha_alta !== undefined) {
      updateData.fecha_alta = input.fecha_alta
    }
    if (input.motivo_baja !== undefined) {
      updateData.motivo_baja = input.motivo_baja
    }
    if (input.motivo_baja_detalle !== undefined) {
      updateData.motivo_baja_detalle = input.motivo_baja_detalle
    }
    if (input.descripcion !== undefined) {
      updateData.descripcion = input.descripcion
    }

    const { data: clienteData, error: errorCliente } = await client
      .from('clientes')
      .update(updateData as never)
      .eq('id', clienteId)
      .select()
      .single()

    if (errorCliente) throw errorCliente

    // 3. Actualizar o crear faceta contable
    if (facetaContable?.id) {
      const facetaUpdateData: Record<string, unknown> = {}

      if (input.condicion_iva !== undefined) {
        facetaUpdateData.condicion_iva = mapCondicionFiscal(input.condicion_iva)
      }
      if (input.categoria_monotributo !== undefined) {
        facetaUpdateData.categoria_monotributo = input.categoria_monotributo
      }
      if (input.actividad_principal !== undefined) {
        facetaUpdateData.actividad_principal = input.actividad_principal
      }
      if (input.codigo_actividad_afip !== undefined) {
        facetaUpdateData.codigo_actividad_afip = input.codigo_actividad_afip
      }
      if (input.actividades_economicas !== undefined) {
        facetaUpdateData.actividades_economicas = input.actividades_economicas
      }
      if (input.cierre_ejercicio !== undefined) {
        facetaUpdateData.cierre_ejercicio = input.cierre_ejercicio
      }
      if (input.honorarios_mensuales !== undefined) {
        facetaUpdateData.honorarios_mensuales = input.honorarios_mensuales
      }
      if (input.clave_fiscal !== undefined) {
        facetaUpdateData.clave_fiscal = input.clave_fiscal
      }
      if (input.impuestos_inscriptos !== undefined) {
        facetaUpdateData.impuestos_inscriptos = input.impuestos_inscriptos
      }
      if (input.regimen_iibb !== undefined) {
        facetaUpdateData.regimen_iibb = input.regimen_iibb
        if (input.regimen_iibb === 'local') {
          facetaUpdateData.provincia_principal = input.domicilio_provincia || input.provincia_principal
        }
      }
      if (input.provincia_principal !== undefined && input.regimen_iibb !== 'local') {
        facetaUpdateData.provincia_principal = input.provincia_principal
      }
      if (input.impuestos_liquidar !== undefined) {
        facetaUpdateData.impuestos_liquidar = input.impuestos_liquidar
      }
      // Datos de Inscripción
      if (input.inscripcion_numero !== undefined) {
        facetaUpdateData.inscripcion_numero = input.inscripcion_numero
      }
      if (input.inscripcion_fojas !== undefined) {
        facetaUpdateData.inscripcion_fojas = input.inscripcion_fojas
      }
      if (input.inscripcion_tomo !== undefined) {
        facetaUpdateData.inscripcion_tomo = input.inscripcion_tomo
      }
      if (input.inscripcion_protocolo !== undefined) {
        facetaUpdateData.inscripcion_protocolo = input.inscripcion_protocolo
      }
      if (input.inscripcion_fecha !== undefined) {
        facetaUpdateData.inscripcion_fecha = input.inscripcion_fecha
      }
      if (input.inscripcion_ano !== undefined) {
        facetaUpdateData.inscripcion_ano = input.inscripcion_ano
      }

      const { error: errorFaceta } = await client
        .from('cliente_faceta_contable')
        .update(facetaUpdateData as never)
        .eq('id', facetaContable.id)

      if (errorFaceta) throw errorFaceta
    } else {
      const provinciaParaIIBB = input.regimen_iibb === 'local'
        ? (input.domicilio_provincia || input.provincia_principal)
        : input.provincia_principal

      const { error: errorFaceta } = await client
        .from('cliente_faceta_contable')
        .insert({
          cliente_id: clienteId,
          condicion_iva: mapCondicionFiscal(input.condicion_iva),
          categoria_monotributo: input.categoria_monotributo || null,
          actividad_principal: input.actividad_principal || null,
          codigo_actividad_afip: input.codigo_actividad_afip || null,
          actividades_economicas: input.actividades_economicas || [],
          cierre_ejercicio: input.cierre_ejercicio || null,
          honorarios_mensuales: input.honorarios_mensuales || 0,
          clave_fiscal: input.clave_fiscal || null,
          provincia_principal: provinciaParaIIBB || null,
          impuestos_inscriptos: input.impuestos_inscriptos || null,
          regimen_iibb: input.regimen_iibb || null,
          impuestos_liquidar: input.impuestos_liquidar || [],
          // Datos de Inscripción
          inscripcion_numero: input.inscripcion_numero || null,
          inscripcion_fojas: input.inscripcion_fojas || null,
          inscripcion_tomo: input.inscripcion_tomo || null,
          inscripcion_protocolo: input.inscripcion_protocolo || null,
          inscripcion_fecha: input.inscripcion_fecha || null,
          inscripcion_ano: input.inscripcion_ano || null,
        } as never)

      if (errorFaceta) throw errorFaceta
    }

    // Sincronizar impuestos con tabla impuestos_cliente
    if (input.impuestos_liquidar !== undefined) {
      const impuestoIds = (input.impuestos_liquidar || []).map((i) => i.impuesto_id)
      const fechasInscripcion = Object.fromEntries(
        (input.impuestos_liquidar || [])
          .filter((i) => i.fecha_inscripcion)
          .map((i) => [i.impuesto_id, i.fecha_inscripcion ?? null])
      )
      await impuestosClienteService.sincronizar(client, clienteId, impuestoIds, fechasInscripcion)
    }

    // Sync responsables
    if (input.responsables !== undefined) {
      await this.sincronizarResponsables(client, clienteId, input.responsables || [])
    }

    return clienteData as Cliente
  },

  async crear(client: SupabaseClient<Database>, clienteData: ClienteInsert): Promise<Cliente> {
    // Insert sin RETURNING + fetch posterior (ver comentario en crearCompleto).
    const nuevoClienteId = clienteData.id ?? crypto.randomUUID()
    const { error } = await client
      .from('clientes')
      .insert({ ...clienteData, id: nuevoClienteId } as never)

    if (error) throw error

    const { data, error: errorFetch } = await client
      .from('clientes')
      .select()
      .eq('id', nuevoClienteId)
      .single()

    if (errorFetch) throw errorFetch
    return data as Cliente
  },

  async actualizar(client: SupabaseClient<Database>, id: string, clienteData: ClienteUpdate): Promise<Cliente> {
    const { data, error } = await client
      .from('clientes')
      .update(clienteData as never)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as Cliente
  },

  /**
   * ORR-233: patch quirúrgico de la faceta contable, para la re-lectura del padrón.
   *
   * No usa `actualizarCompleto`: ese toca persona, cliente, impuestos y responsables,
   * y acá solo hay que escribir los campos fiscales que trajo ARCA.
   *
   * `cliente_faceta_contable` no tiene `organizacion_id` —cuelga de `clientes`—, así
   * que el scope multi-tenant se verifica sobre el cliente antes de escribir. Sin ese
   * paso el `.eq('cliente_id', …)` alcanzaría cualquier faceta de cualquier organización.
   */
  async actualizarFacetaContable(
    client: SupabaseClient<Database>,
    clienteId: string,
    organizacionId: string,
    patch: Record<string, unknown>
  ): Promise<void> {
    if (Object.keys(patch).length === 0) return

    const { data: cliente, error: errorCliente } = await client
      .from('clientes')
      .select('id')
      .eq('id', clienteId)
      .eq('organizacion_id', organizacionId)
      .maybeSingle()

    if (errorCliente) throw errorCliente
    if (!cliente) throw new Error('El cliente no pertenece a la organización activa')

    const { data: actualizadas, error } = await client
      .from('cliente_faceta_contable')
      .update(patch as never)
      .eq('cliente_id', clienteId)
      .select('id')

    if (error) throw error

    // Un cliente sin faceta todavía: crearla en vez de perder el dato en silencio.
    if (!actualizadas || actualizadas.length === 0) {
      const { error: errorInsert } = await client
        .from('cliente_faceta_contable')
        .insert({ cliente_id: clienteId, ...patch } as never)
      if (errorInsert) throw errorInsert
    }
  },

  async eliminar(client: SupabaseClient<Database>, id: string): Promise<void> {
    // RPC con RBAC: verifica rol del caller antes de eliminar
    const { error } = await client.rpc('eliminar_cliente_seguro', { p_id: id })
    if (error) throw error
  },

  async obtenerEstadoCuenta(client: SupabaseClient<Database>, clienteId: string): Promise<Movimiento[]> {
    const { data, error } = await client
      .from('movimientos')
      .select('*')
      .eq('cliente_id', clienteId)
      .order('fecha', { ascending: false })

    if (error) throw error
    return data as Movimiento[]
  },

  async obtenerResponsables(client: SupabaseClient<Database>, clienteId: string): Promise<string[]> {
    const { data, error } = await client
      .from('cliente_responsables')
      .select('usuario_id')
      .eq('cliente_id', clienteId)
    if (error) throw error
    return data?.map(r => r.usuario_id) || []
  },

  async sincronizarResponsables(client: SupabaseClient<Database>, clienteId: string, usuarioIds: string[]): Promise<void> {
    // Delete existing
    const { error: deleteError } = await client
      .from('cliente_responsables')
      .delete()
      .eq('cliente_id', clienteId)
    if (deleteError) throw deleteError

    // Insert new (deduplicate to avoid UNIQUE constraint violation)
    const uniqueIds = [...new Set(usuarioIds)]
    if (uniqueIds.length > 0) {
      const { error: insertError } = await client
        .from('cliente_responsables')
        .insert(uniqueIds.map(usuario_id => ({
          cliente_id: clienteId,
          usuario_id,
        })))
      if (insertError) throw insertError
    }
  },

  /**
   * Agrega UN responsable a un cliente sin tocar los demás (idempotente).
   * A diferencia de `sincronizarResponsables` (delete-all + insert), no borra el resto.
   * ORR-202: usado al delegar una tarea a un usuario que no tiene ese cliente.
   */
  async agregarResponsable(client: SupabaseClient<Database>, clienteId: string, usuarioId: string): Promise<void> {
    const { error } = await client
      .from('cliente_responsables')
      .upsert(
        { cliente_id: clienteId, usuario_id: usuarioId },
        { onConflict: 'cliente_id,usuario_id', ignoreDuplicates: true },
      )
    if (error) throw error
  },

  async obtenerServicios(client: SupabaseClient<Database>, clienteId: string): Promise<string[]> {
    const { data, error } = await client
      .from('cliente_servicio_precios')
      .select('servicio_id')
      .eq('cliente_id', clienteId)
      .eq('activo', true)

    if (error) throw error
    return data?.map((d: { servicio_id: string }) => d.servicio_id) || []
  },
}
